import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { getFeatureCatalog, getFeatureCategories, getLimitCatalog, getCountries, getTaxRegimes, updateFeatureCatalog, type FeatureCatalog, type FeatureCategoryCatalog } from '@/services'
import { AppButton, EmptyState, Modal, Tabs, TableSkeleton } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'

export function CatalogsPage() {
  const qc = useQueryClient()
  const [tab, setTab] = useState('features')
  const [editingFeature, setEditingFeature] = useState<FeatureCatalog | null>(null)

  const { data: features = [], isLoading: featuresLoading } = useQuery({ queryKey: ['feature-catalog'], queryFn: getFeatureCatalog })
  const { data: featureCategories = [] } = useQuery({ queryKey: ['feature-categories'], queryFn: () => getFeatureCategories() })
  const { data: limits = [], isLoading: limitsLoading } = useQuery({ queryKey: ['limit-catalog'], queryFn: getLimitCatalog })
  const { data: countries = [], isLoading: countriesLoading } = useQuery({ queryKey: ['countries'], queryFn: getCountries })
  const { data: taxRegimes = [], isLoading: taxRegimesLoading } = useQuery({ queryKey: ['tax-regimes', 'all'], queryFn: () => getTaxRegimes() })

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Plataforma</div>
          <h1>Catálogos</h1>
          <div className="subtitle">Listas de referencia que usa el resto del panel.</div>
        </div>
      </div>

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'features', label: 'Features', icon: 'zap', count: features.length },
          { id: 'limits', label: 'Limits', icon: 'package', count: limits.length },
          { id: 'countries', label: 'Países', icon: 'globe', count: countries.length },
          { id: 'taxes', label: 'Regímenes fiscales', icon: 'briefcase', count: taxRegimes.length },
        ]}
      />

      {tab === 'features' ? (
        <div className="card">
          <div className="card-body flush">
            {featuresLoading ? <TableSkeleton columns={5} rows={8} /> : <table className="table">
              <thead><tr><th>Slug</th><th>Nombre</th><th>Descripción</th><th>Categoría</th><th /></tr></thead>
              <tbody>
                {features.map((f) => (
                  <tr key={f.key} onClick={() => setEditingFeature(f)}>
                    <td>
                      <div className="row gap-2">
                        <div style={{ width: 24, height: 24, background: 'var(--surface-3)', borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-2)' }}>
                          <Icon name={f.icon || 'zap'} size={13} />
                        </div>
                        <span className="code-chip">{f.key}</span>
                      </div>
                    </td>
                    <td><span className="cell-primary">{f.name}</span></td>
                    <td><span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{f.description || '—'}</span></td>
                      <td><span className="badge badge-outline badge-sm">{f.category}</span></td>
                      <td style={{ textAlign: 'right' }}><Icon name="chevronRight" size={14} /></td>
                    </tr>
                  ))}
              </tbody>
            </table>}
          </div>
        </div>
      ) : null}

      {tab === 'limits' ? (
        <div className="card">
          <div className="card-body flush">
            {limitsLoading ? <TableSkeleton columns={5} rows={8} /> : <table className="table">
              <thead><tr><th>Key</th><th>Nombre</th><th>Descripción</th><th>Unidad</th><th>Valor ilimitado</th></tr></thead>
              <tbody>
                {limits.map((l) => (
                  <tr key={l.key}>
                    <td><span className="code-chip">{l.key}</span></td>
                    <td><span className="cell-primary">{l.name}</span></td>
                    <td><span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{l.description || '—'}</span></td>
                    <td><span className="badge badge-outline badge-sm">{l.unit || '—'}</span></td>
                    <td><span className="tabular">{l.unlimited_value}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>}
          </div>
        </div>
      ) : null}

      {tab === 'countries' ? (
        <div className="card">
          <div className="card-body flush">
            {countriesLoading ? <TableSkeleton columns={4} rows={8} /> : <table className="table">
              <thead><tr><th>Código</th><th>Nombre</th><th>Currency</th><th>Locale</th></tr></thead>
              <tbody>
                {countries.map((c) => (
                  <tr key={c.id}>
                    <td><span className="code-chip" style={{ fontWeight: 600 }}>{c.code}</span></td>
                    <td><span className="cell-primary">{c.name}</span></td>
                    <td><span className="badge badge-outline badge-sm">{c.currency_code}</span></td>
                    <td><span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{c.locale}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>}
          </div>
        </div>
      ) : null}

      {tab === 'taxes' ? (
        taxRegimesLoading ? (
          <div className="card"><div className="card-body flush"><TableSkeleton columns={3} rows={8} /></div></div>
        ) : taxRegimes.length === 0 ? (
          <div className="card"><div className="card-body"><EmptyState icon="briefcase" title="Sin regímenes" desc="Elegí un país con regímenes fiscales cargados." /></div></div>
        ) : (
          <div className="card">
            <div className="card-body flush">
              <table className="table">
                <thead><tr><th>Código</th><th>Nombre</th><th>Descripción</th></tr></thead>
                <tbody>
                  {taxRegimes.map((t) => (
                    <tr key={t.id}>
                      <td><span className="code-chip" style={{ fontWeight: 600 }}>{t.code}</span></td>
                      <td><span className="cell-primary">{t.name}</span></td>
                      <td><span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{t.description || '—'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : null}

      {editingFeature ? (
        <FeatureFormModal
          feature={editingFeature}
          categories={featureCategories}
          onClose={() => setEditingFeature(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['feature-catalog'] }); setEditingFeature(null) }}
        />
      ) : null}
    </>
  )
}

function FeatureFormModal({ feature, categories, onClose, onSaved }: { feature: FeatureCatalog; categories: FeatureCategoryCatalog[]; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(feature.name)
  const [description, setDescription] = useState(feature.description || '')
  const [category, setCategory] = useState(feature.category || 'general')
  const [icon, setIcon] = useState(feature.icon || '')
  const [sortOrder, setSortOrder] = useState(String(feature.sort_order || 0))
  const [isActive, setIsActive] = useState(feature.is_active !== false)

  const saveMutation = useMutation({
    mutationFn: () => updateFeatureCatalog(feature.key, {
      name: name.trim(),
      description: description.trim() || null,
      category: category.trim() || 'general',
      icon: icon.trim() || null,
      sort_order: parseInt(sortOrder, 10) || 0,
      is_active: isActive,
    }),
    onSuccess: () => { toast.success('Feature actualizada'); onSaved() },
    onError: (error: any) => toast.error(error?.message || 'No se pudo guardar la feature'),
  })

  return (
    <Modal
      open
      onClose={onClose}
      title={`Editar ${feature.key}`}
      subtitle="La categoría se usa para agrupar features en el catálogo."
      footer={
        <div className="row gap-2" style={{ justifyContent: 'flex-end' }}>
          <AppButton variant="ghost" onClick={onClose}>Cancelar</AppButton>
          <AppButton variant="primary" icon="check" disabled={!name.trim() || !category.trim() || saveMutation.isPending} onClick={() => saveMutation.mutate()}>{saveMutation.isPending ? 'Guardando...' : 'Guardar feature'}</AppButton>
        </div>
      }
    >
      <div className="col gap-4">
        <div className="form-row">
          <label>Key</label>
          <span className="code-chip" style={{ width: 'fit-content' }}>{feature.key}</span>
        </div>
        <div className="form-row"><label htmlFor="feature-name">Nombre</label><input id="feature-name" className="input" value={name} onChange={(event) => setName(event.target.value)} /></div>
        <div className="form-row"><label htmlFor="feature-description">Descripción</label><input id="feature-description" className="input" value={description} onChange={(event) => setDescription(event.target.value)} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px', gap: 14 }}>
          <div className="form-row">
            <label htmlFor="feature-category">Categoría</label>
            <select id="feature-category" className="select" value={category} onChange={(event) => setCategory(event.target.value)}>
              {categories.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
          </div>
          <div className="form-row"><label htmlFor="feature-icon">Icono</label><input id="feature-icon" className="input" value={icon} onChange={(event) => setIcon(event.target.value)} /></div>
          <div className="form-row"><label htmlFor="feature-order">Orden</label><input id="feature-order" className="input" type="number" value={sortOrder} onChange={(event) => setSortOrder(event.target.value)} /></div>
        </div>
        <label className="toggle"><input type="checkbox" checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />Activa</label>
      </div>
    </Modal>
  )
}
